import React, { useState, useCallback, useRef } from 'react';
import { uploadFile, useUploadUrlMutation } from '../api';
import { v4 as uuidv4 } from 'uuid';

const UploadForm = ({ onUploadComplete, onUploadTabActivate }) => {
  const [uploading, setUploading] = useState(false);
  const [fileStatuses, setFileStatuses] = useState([]);
  const [preparedFiles, setPreparedFiles] = useState([]);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [processingFiles, setProcessingFiles] = useState(false);
  const fileInputRef = useRef(null);

  // Clean up previews on unmount
  React.useEffect(() => {
    return () => {
      fileStatuses.forEach(f => f.preview && URL.revokeObjectURL(f.preview));
      preparedFiles.forEach(f => f.preview && URL.revokeObjectURL(f.preview));
    };
  }, [fileStatuses, preparedFiles]);

  // Helper function to determine if a file is a video
  const isVideo = (file) => file.type.startsWith('video/');

  // Helper function to determine if a file is an image
  const isImage = (file) => file.type.startsWith('image/');

  const onDrop = useCallback((acceptedFiles) => {
    // Immediately close the native picker by resetting the input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    setPreparedFiles([]);
    setShowConfirmation(false);
    setProcessingFiles(true);

    // Validate files first
    const validFiles = [];
    let invalidFile = null;
    let invalidReason = '';

    const MAX_FILE_SIZE = 500 * 1024 * 1024; // 500MB in bytes

    for (const file of acceptedFiles) {
      if (!isImage(file) && !isVideo(file)) {
        invalidFile = file;
        invalidReason = 'File type not supported';
        break;
      }
      if (file.size > MAX_FILE_SIZE) {
        invalidFile = file;
        invalidReason = `File too large (max 500MB)`;
        break;
      }
      validFiles.push(file);
    }

    if (invalidFile) {
      setProcessingFiles(false);
      alert(`${invalidReason}: ${invalidFile.name}`);
      return;
    }

    // Only process and preview files, no thumbnail or jpeg generation
    const processedFiles = [];
    for (const file of validFiles) {
      const preview = URL.createObjectURL(file);
      const fileType = isVideo(file) ? 'video' : 'image';
      processedFiles.push({
        file,
        preview,
        fileType,
        name: file.name,
        size: file.size,
        id: Date.now() + Math.random()
      });
    }
    setPreparedFiles(processedFiles);
    setShowConfirmation(true);
    setProcessingFiles(false);
  }, []);

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    if (files.length > 0) {
      onDrop(files);
    }
  };
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const files = Array.from(e.dataTransfer.files);
    onDrop(files);
  };

  const removeFile = (fileId) => {
    setPreparedFiles(prev => {
      const fileToRemove = prev.find(f => f.id === fileId);
      if (fileToRemove && fileToRemove.preview) {
        URL.revokeObjectURL(fileToRemove.preview);
      }
      return prev.filter(f => f.id !== fileId);
    });
  };

  // Use React Query mutation for upload URLs
  const uploadUrlMutation = useUploadUrlMutation();

  // Only upload the original file with batched uploads for better performance
  const confirmUpload = async () => {
    setUploading(true);
    setFileStatuses([]);
    setShowConfirmation(false);

    const uploadStatuses = preparedFiles.map(file => ({
      name: file.name,
      status: 'uploading',
      error: null,
      preview: file.preview,
      progress: 0,
      fileType: file.fileType,
      id: file.id
    }));
    setFileStatuses(uploadStatuses);

    // Upload files in batches of 5 to prevent overwhelming the browser/network
    const BATCH_SIZE = 3;
    const batches = [];
    for (let i = 0; i < preparedFiles.length; i += BATCH_SIZE) {
      batches.push(preparedFiles.slice(i, i + BATCH_SIZE));
    }

    // Process each batch sequentially
    for (const batch of batches) {
      await Promise.all(batch.map(async (preparedFile) => {
        try {
          const fileUuid = uuidv4();
          const timestamp = Date.now();
          const ext = preparedFile.name.split('.').pop();
          const safeName = `${timestamp}_${fileUuid}.${ext}`;

          // Request signed URL using React Query mutation
          const { url } = await uploadUrlMutation.mutateAsync({
            fileName: safeName,
            contentType: preparedFile.file.type
          });

          await uploadFile(url, preparedFile.file, (progress) => {
            setFileStatuses(prev => prev.map(f =>
              f.id === preparedFile.id ? { ...f, progress } : f
            ));
          });

          setFileStatuses(prev => prev.map(f =>
            f.id === preparedFile.id ? { ...f, status: 'success', progress: 100 } : f
          ));
        } catch (err) {
          setFileStatuses(prev => prev.map(f =>
            f.id === preparedFile.id ? { ...f, status: 'error', error: err.message } : f
          ));
        }
      }));
    }

    setUploading(false);

    setFileStatuses(prev => {
      const successFiles = prev.filter(f => f.status === 'success');
      if (successFiles.length > 0 && onUploadComplete) {
        onUploadComplete(successFiles);
      }
      return prev;
    });
  };

  const cancelUpload = () => {
    setPreparedFiles([]);
    setShowConfirmation(false);
    setFileStatuses([]);
  };

  return (
    <div className="upload-container">
      {/* Processing Overlay */}
      {processingFiles && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl px-8 py-8 flex flex-col items-center max-w-sm mx-4">
            <div className="relative mb-6">
              <svg className="animate-spin h-12 w-12 text-wedding-sage" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
              </svg>
              <div className="absolute inset-0 rounded-full border-2 border-wedding-sage/20 animate-ping"></div>
            </div>
            <h3 className="text-wedding-dark-brown font-semibold text-xl mb-2 text-center">Processing your files...</h3>
            <p className="text-wedding-dark-brown/70 text-sm text-center leading-relaxed">
              We're preparing your beautiful memories for upload. This may take a moment.
            </p>
            <div className="mt-6 w-full bg-wedding-sage/10 rounded-full h-3 overflow-hidden">
              <div className="bg-gradient-to-r from-wedding-sage to-wedding-sage/80 h-full rounded-full animate-pulse" style={{ width: '70%' }}></div>
            </div>
            <div className="mt-4 text-xs text-wedding-dark-brown/50 text-center">
              Please don't close this page
            </div>
          </div>
        </div>
      )}

      {/* Drag and Drop Area - Only show when no files are prepared */}
      {!showConfirmation && !processingFiles && (
        <div
          className={`border-2 border-dashed border-wedding-sage rounded-xl p-8 text-center bg-wedding-sage/10 transition-all duration-200 ${uploading || processingFiles ? 'opacity-50 pointer-events-none' : 'hover:bg-wedding-sage/20'}`}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*,video/*"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
            disabled={processingFiles}
          />


          <div className="upload-content">
            <svg className="mx-auto h-12 w-12 text-wedding-sage mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>

            <p className="text-wedding-dark-brown font-medium text-lg mb-2">
              {uploading ? 'Uploading...' : 'Drop photos and videos here'}
            </p>

            <p className="text-wedding-dark-brown/70 text-sm mb-4">
              {uploading ? 'Please wait while files are being processed...' : 'Capture or share your favourite moments!'}
            </p>

            {/* Single upload button that opens native picker */}
            <div className="flex justify-center">
              <button
                onClick={() => {
                  if (!uploading && !processingFiles) {
                    onUploadTabActivate && onUploadTabActivate();
                    fileInputRef.current?.click();
                  }
                }}
                disabled={uploading || processingFiles}
                className="flex items-center justify-center gap-2 px-8 py-3 bg-wedding-sage text-white rounded-lg hover:bg-wedding-sage/80 transition-colors disabled:opacity-50"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                Choose Photos & Videos
              </button>
            </div>

            <p className="text-wedding-sage text-xs mt-4">
              Image and Video uploads Only (max 500MB each)
            </p>
          </div>
        </div>
      )}

      {/* Prepared Files Grid - Show when files are ready for confirmation */}
      {showConfirmation && preparedFiles.length > 0 && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <div className="grid grid-cols-1">
              <h3 className="font-bold text-wedding-sage text-lg">
                Ready<br />
              </h3>
              <p className="text-wedding-dark-brown/70 text-sm">
                ({preparedFiles.length} file{preparedFiles.length > 1 ? 's' : ''})
              </p>
            </div>
            <div className="flex gap-1">
              <button
                onClick={cancelUpload}
                className="px-4 py-2 text-wedding-dark-brown hover:text-wedding-sage transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmUpload}
                disabled={uploading}
                className="px-6 py-2 bg-wedding-sage text-white rounded-lg hover:bg-wedding-sage/80 transition-colors disabled:opacity-50"
              >
                {uploading ? 'Uploading...' : 'Upload'}
              </button>
            </div>
          </div>

          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-1">
            {preparedFiles.map((file) => (
              <div key={file.id} className="relative group aspect-square overflow-hidden">
                {file.fileType === 'video' ? (
                  // Video preview
                  <div className="video-thumbnail relative w-full h-full">
                    <video
                      src={file.preview}
                      className="w-full h-full object-cover rounded shadow-md"
                      muted
                      preload="metadata"
                    />
                    {/* Video indicator */}
                    <div className="absolute top-2 right-2 bg-black bg-opacity-70 text-white text-xs px-2 py-1 rounded">
                      <svg className="w-3 h-3 inline mr-1" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z" />
                      </svg>
                      VIDEO
                    </div>
                    {/* Play button overlay
                    <div className="play-button">
                      <svg className="w-6 h-6 text-wedding-dark-brown" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M8 5v14l11-7z"/>
                      </svg>
                    </div> */}
                  </div>
                ) : (
                  // Image preview
                  <img
                    src={file.preview}
                    alt={file.name}
                    className="w-full h-full object-cover rounded shadow-md"
                  />
                )}

                {/* Remove button */}
                <button
                  onClick={() => removeFile(file.id)}
                  className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-70 text-white rounded-full w-8 h-8 m-auto text-lg opacity-70 hover:opacity-100 transition-all focus:outline-none"
                  style={{ pointerEvents: 'auto' }}
                  aria-label="Remove file"
                >
                  ×
                </button>

                {/* File info */}
                <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 px-2 py-1 text-xs text-white truncate">
                  {file.name}
                </div>

                {/* Thumbnail ready indicator */}
                {file.thumbnail && (
                  <div className="absolute top-2 right-2 bg-wedding-sage text-white rounded px-2 py-1 text-xs shadow-md opacity-90">
                    Ready
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Upload Progress Display */}
      {fileStatuses.length > 0 && (
        <div className="mt-6">
          <h3 className="font-bold mb-4 text-wedding-sage text-lg">Upload Progress:</h3>
          {/* Disclaimer for successful uploads */}
          {fileStatuses.some(f => f.status === 'success') && !fileStatuses.some(f => f.status === 'uploading' || f.status === 'error') && (
            <div className="bg-wedding-sage-hint/20 border border-wedding-sage rounded-lg p-4 mb-4">
              <div className="flex items-start">
                <svg className="w-5 h-5 text-wedding-dark-brown mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                <div>
                  <p className="text-wedding-dark-brown font-medium text-sm">Photos uploaded successfully!</p>
                  <p className="text-wedding-dark-brown/70  text-sm mt-1">
                    It may take a few minutes before your photos appear in the gallery tab. Please check back later.
                  </p>
                </div>
              </div>
            </div>
          )}
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-1">
            {fileStatuses.map((file) => (
              <div key={file.id} className="relative group aspect-square overflow-hidden">
                {file.fileType === 'video' ? (
                  <div className="video-thumbnail relative w-full h-full">
                    <video
                      src={file.preview}
                      className={`w-full h-full object-cover ${file.status === 'error' ? 'opacity-50 grayscale' : ''}`}
                      muted
                      preload="metadata"
                    />
                    <div className="absolute top-2 right-2 bg-black bg-opacity-70 text-white text-xs px-2 py-1 rounded">
                      <svg className="w-3 h-3 inline mr-1" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z" />
                      </svg>
                      VIDEO
                    </div>
                  </div>
                ) : (
                  <img
                    src={file.preview}
                    alt={file.name}
                    className={`w-full h-full object-cover ${file.status === 'error' ? 'opacity-50 grayscale' : ''}`}
                  />
                )}

                {/* Overlay for progress or status */}
                {file.status === 'uploading' && (
                  <div className="absolute inset-0 bg-black bg-opacity-40 flex flex-col items-center justify-center">
                    <svg className="animate-spin h-6 w-6 text-wedding-sage mb-2" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
                    </svg>
                    <span className="text-white font-semibold">{file.progress || 0}%</span>
                  </div>
                )}

                {file.status === 'success' && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="bg-wedding-sage bg-opacity-70 rounded-full p-2">
                      <svg className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  </div>
                )}

                {file.status === 'error' && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center bg-red-600 bg-opacity-60">
                    <svg className="h-8 w-8 text-white mb-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    <span className="text-white text-xs font-semibold text-center px-2">{file.error}</span>
                  </div>
                )}

                {/* File info */}
                <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 px-2 py-1 text-xs text-white truncate">
                  {file.name}
                </div>

                {/* Progress bar */}
                {file.status === 'uploading' && (
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-wedding-sage/30">
                    <div
                      className="h-full bg-wedding-sage transition-all duration-300"
                      style={{ width: `${file.progress}%` }}
                    ></div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default UploadForm;